import cn from "classnames";
import React, { ChangeEvent } from "react";
import DOMPurify from "dompurify";
import {
  Button,
  Heading,
  Textarea,
  useMediaQuery,
} from "@rshbintech.dboul/core-ui";
import { Rating } from "../feedback-form-content/rating";
import { ComponentsFromSchemaProps } from "./types";
import styles from "./styles.scss";

export const ComponentsFromSchema = ({
  schema,
  form,
  feedbackFormType,
  isSubmitDisabled,
  onChange,
  onSubmit,
}: ComponentsFromSchemaProps) => {
  const isMobile = useMediaQuery("mobile");
  const isCsiForm = feedbackFormType === "CSI";

  const components = schema.map((elementProps, index) => {
    switch (elementProps.component) {
      case "heading":
        return React.createElement(Heading, {
          key: index,
          className: cn(styles.heading, styles[feedbackFormType.toLowerCase()]),
          ...elementProps,
        });

      case "text":
        return React.createElement("p", {
          key: index,
          className: cn(
            styles.text,
            styles[feedbackFormType.toLowerCase()],
            styles[elementProps.size || "medium"]
          ),
          dangerouslySetInnerHTML: {
            __html: DOMPurify.sanitize(elementProps.children as string),
          },
        });

      case "rating":
        return React.createElement(Rating, {
          key: index,
          className: styles.rating,
          onChange: (_, value) => {
            onChange?.(elementProps.componentId, value);
          },
          ...elementProps,
          defaultValue: (() => {
            if (!isCsiForm && typeof form.score === "number") {
              return form.score;
            }

            if (Array.isArray(form.score)) {
              const id = elementProps.componentId.split("-")[2];
              const ratingType = elementProps.componentId.split("-")[1] as
                | "importance"
                | "satisfaction";

              const scoreById = form.score.find((item) => item.id === id);

              return scoreById ? scoreById[ratingType] : -1;
            }

            return -1;
          })(),
        });

      case "textarea":
        return React.createElement(Textarea, {
          key: index,
          className: cn(
            styles.textarea,
            styles[feedbackFormType.toLowerCase()]
          ),
          ...elementProps,
          value: form.comment,
          onChange: (event: ChangeEvent<HTMLTextAreaElement>) => {
            const { value } = event.target;

            onChange?.(elementProps.componentId, value);
          },
        });

      case "button":
        return feedbackFormType === "CSI" && !isMobile
          ? null
          : React.createElement(Button, {
              key: index,
              disabled: isSubmitDisabled,
              className: cn(
                styles.button,
                styles[feedbackFormType.toLowerCase()]
              ),
              ...elementProps,
              onClick: onSubmit,
            });

      case "block": {
        const children = elementProps.children.map((child, childIndex) => (
          <ComponentsFromSchema
            key={childIndex}
            form={form}
            schema={[child]}
            feedbackFormType={feedbackFormType}
            onChange={onChange}
          />
        ));

        return (
          <div
            key={index}
            className={cn(styles.block, styles[elementProps.direction])}
          >
            {children}
          </div>
        );
      }

      default:
        return null;
    }
  });

  return <>{components}</>;
};
